import { sortByKey } from '@semios/app-platform-common'
import { calculatePlanningRows } from 'App/irrigation-scheduler/components/Grid/components/IrrigationZoneEmitterRows/components/_utils/calculatePlanningRows'
import { translate } from 'i18n/i18n'
import moment from 'moment-timezone'
import React, { useMemo } from 'react'
import { colors } from 'settings/colors'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import type { TIrrigationZoneEmitterId } from 'stores/fieldAssetStore/helperTypes'
import { selectedFieldAssetsStore } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { unitConverter } from 'utils/unitConverter/unitConverter'
import { buildDateGrid } from '../_utils/buildDateGrid'
import { getAlternatingRowStyle, icsTableStyles } from '../_utils/icsTableStyles'
import { getZoneEvents } from '../_utils/parseZoneEvents'
import { useIrrigationZoneNameDictionary } from '../_utils/useIrrigationZoneNameDictionary'
import { DayColumnHeader } from './DayColumnHeader'
import { PlanningDataRow } from './PlanningDataRow'
import type { TCombinedTableThreeRowProps, TZoneRow } from './types'

export const IrrigationPlannerTableRows = ({
  eventsResponse,
  parsedEvents,
  dailyEtData,
  emitterType,
  dateRange,
  showApplied,
}: TCombinedTableThreeRowProps) => {
  const selectedPropertyId = selectedFieldAssetsStore.useSelector((s) => s.property)
  const selectedProperty = fieldAssetStore.useSelector((s) => s.properties)?.[Number(selectedPropertyId)]
  const irrigationZoneEmitters = selectedProperty?.irrigationZoneEmitters ?? {}
  const depthUnit = unitConverter.evapotranspiration().suffix()
  const irrigationZoneNameDictionary = useIrrigationZoneNameDictionary(irrigationZoneEmitters)

  const computed = useMemo(() => {
    if (!selectedProperty?.timezone) return null

    const { timezone } = selectedProperty

    const zoneIds = [
      ...new Set(
        eventsResponse?.filter((ze) => ze.emitterType === emitterType).map((ze) => ze.irrigationZoneId) ?? [],
      ),
    ]

    const zones = zoneIds
      .map((zoneId) => ({
        zoneId,
        zoneName: irrigationZoneNameDictionary[zoneId] ?? `Zone ${zoneId}`,
      }))
      .sort(sortByKey('zoneName'))

    if (zones.length === 0) return null

    const dates = buildDateGrid(dateRange, timezone)
    const viewStart = moment.tz(dates[0], timezone).startOf('day')
    const viewEnd = viewStart.clone().add(dates.length, 'days')

    const zoneRows: TZoneRow[] = zones.map((zone) => {
      const zoneEmitterKey = `${zone.zoneId}|||${emitterType}` as TIrrigationZoneEmitterId
      const flowRate = irrigationZoneEmitters[zoneEmitterKey]?.flowRate ?? {}

      const zoneEtcData = dailyEtData.find((z) => z.zone_id === zone.zoneId) ?? {
        zone_id: zone.zoneId,
        blockData: { block_ids: [], kc: [], etc: [], rain: [] },
      }

      const { scheduledEvents, scheduledPulseEvents, appliedEvents } = getZoneEvents(
        parsedEvents,
        zoneEmitterKey,
      )

      const mmPerHour = flowRate.mmPerHour ?? 0
      const rateConverter = unitConverter.irrigationApplicationRate(mmPerHour)
      const displayRate = rateConverter.value() ?? 0

      return {
        zoneId: zone.zoneId,
        zoneName: zone.zoneName,
        rate: displayRate,
        unitsPerHour: rateConverter.suffix(),
        rows: calculatePlanningRows({
          zoneEtcData,
          scheduledEvents,
          scheduledPulseEvents,
          appliedEvents: showApplied ? appliedEvents : undefined,
          flowRateMmPerHour: flowRate.mmPerHour ?? 0,
          timezone,
          viewStart,
          viewEnd,
          showUnitInLabel: false,
        }),
      }
    })

    return { dates, timezone, zoneRows }
  }, [
    eventsResponse,
    parsedEvents,
    dailyEtData,
    emitterType,
    dateRange,
    showApplied,
    selectedProperty,
    irrigationZoneEmitters,
    irrigationZoneNameDictionary,
  ])

  if (!computed) return null

  const { dates, timezone, zoneRows } = computed

  return (
    <div css={{ marginBottom: 16 }}>
      <table css={icsTableStyles.table}>
        <thead>
          <tr>
            <th css={{ ...icsTableStyles.headerCell, minWidth: 150 }}>
              {translate.phrases.banyanApp('Zone Name').toUpperCase()}
            </th>
            <th css={{ ...icsTableStyles.headerCellSmall, width: 120 }}>
              {`${translate.phrases.banyanApp('Type').toUpperCase()} (${depthUnit})`}
            </th>
            {dates.map((date) => (
              <th key={date} css={icsTableStyles.headerCell}>
                <DayColumnHeader day={moment.tz(date, timezone)} />
              </th>
            ))}
            <th css={icsTableStyles.headerCell}>{translate.phrases.banyanApp('Total').toUpperCase()}</th>
          </tr>
        </thead>

        <tbody>
          {zoneRows.map((zone, zoneIndex) => {
            const bgColor = getAlternatingRowStyle(zoneIndex, true).backgroundColor

            return (
              <React.Fragment key={zone.zoneId}>
                {zone.rows.map((row, rowIndex) => (
                  <tr key={row.label} css={{ backgroundColor: row.backgroundColor ?? bgColor }}>
                    {rowIndex === 0 && (
                      <td
                        rowSpan={zone.rows.length}
                        css={{ ...icsTableStyles.bodyCellLeft, verticalAlign: 'top' }}
                      >
                        <div>
                          <div css={{ fontWeight: 'bold' }}>{zone.zoneName}</div>
                          <div>
                            {zone.rate > 0 ? (
                              <span css={{ fontSize: 12, color: colors.grey800 }}>
                                {`${zone.rate} ${zone.unitsPerHour}`}
                              </span>
                            ) : (
                              <span css={{ fontSize: 12, color: colors.grey800 }}>
                                {translate.phrases.banyanApp('No Application Rate')}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                    )}
                    <PlanningDataRow row={row} dates={dates} />
                  </tr>
                ))}
              </React.Fragment>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
